import React, { FC, useContext, useEffect, useRef } from 'react';
import Modal from '@components/Modal';
import { useIsMobile } from '@hooks';
import * as ApiModels from '@typings/api-models';
import { getCreatorProducts } from '@tsClient';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import ReorderService from '@modals/ReorderServiceModal/ReorderService';

import teamsContext from '@providers/Teams/TeamsContext';
import Button from '@components/DesignLibrary/Button';
import s from './ReorderServiceModal.module.scss';

type FullProduct = Pick<ApiModels.Product, 'id' | 'title' | 'initiateCaseRank'> & {
  productTypes: Array<Pick<ApiModels.ProductType, 'noodleProductType'>>;
  workflows: Array<Pick<ApiModels.Workflow, 'id' | 'name'>>;
};

type Props = {
  onClose: () => void;
  items: Awaited<ReturnType<typeof getCreatorProducts>>['products'][0][];
  onOrderChange: (updatedProducts: FullProduct[]) => void;
  onSave: () => void;
  fetchingState?: boolean;
};

const ReorderServiceModal: FC<Props> = ({ items, onClose, onOrderChange, onSave, fetchingState }) => {
  const isMobile = useIsMobile();

  const modalBodyRef = useRef<HTMLDivElement>(null);
  const { creatorId } = useContext(teamsContext);
  const {
    data: workflows,
    getData: getWorkflows,
    fetchingState: { isFetching: isFetchingWorkflows },
  } = useNoodleApi(tsClient.workflows.getCreatorWorkflowsSimple);

  useEffect(() => {
    if (!creatorId) {
      return;
    }
    getWorkflows({ creatorId, isActive: true, page: 1, perPage: 1000 });
  }, [creatorId, getWorkflows]);

  const handleClose = (): void => {
    onClose();
  };

  const fullProducts = items?.flatMap(p => {
    const matchedWorkflows = workflows?.
      items?.filter(w => w.referenceId === p.id && w.steps.some(step => step.eventName === ApiModels.WORKFLOW_TRIGGERS.INITIATE_CTA))
      || [];

    if (matchedWorkflows.length && p.isActive) {
      return [{ ...p, workflows: matchedWorkflows }];
    }
    return [];
  }) || [];

  return (
    <Modal
      containerStyle={isMobile ? {} : { height: '80%', width: '80%' }}
      title={'Reorder Service'}
      onClose={handleClose}
      ref={modalBodyRef}
      shouldCloseOnOutsideClick={false}
    >
      <div className={s.container}>
        <ReorderService isFetching={isFetchingWorkflows || !fullProducts} fullProducts={fullProducts} onOrderChange={onOrderChange} />
        <div
          style={{
            marginBottom: 'var(--padding-df)',
          }}
        >
          {!isFetchingWorkflows && (
            <div
              key={'action'}
              style={{
                display: 'flex',
                gap: 'var(--spacer-sm)',
                padding: '0 var(--padding-df)',
              }}
            >
              <Button disabled={fetchingState} size={'md'} fullWidth variant={'secondary'} onClick={handleClose}>
                Cancel
              </Button>
              <Button loading={fetchingState} disabled={fetchingState} size={'md'} fullWidth variant={'primary'} onClick={onSave}>
                Save
              </Button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ReorderServiceModal;
